/**
 * ExerciseRow - Exercise list item in workout detail
 * Supports drag-to-reorder via @dnd-kit when used with SortableList
 * Keyboard accessible: 'g' key triggers selection mode for grouping
 */

import { TrashIcon } from '@heroicons/react/24/outline';
import { forwardRef, memo, useCallback } from 'react';

import type { ExerciseRowProps } from './ExerciseRow.types';

import { IconButton } from '@/components/ui';
import { Badge } from '@/components/ui/Badge';
import { useLongPress } from '@/hooks';

/** The row itself is a `div`, so any `button` ancestor is a nested control. */
function isFromNestedButton(e: React.SyntheticEvent): boolean {
  return e.target instanceof Element && e.target.closest('button') !== null;
}

export const ExerciseRow = memo(
  forwardRef<HTMLDivElement, ExerciseRowProps>(function ExerciseRow(
    {
      exercise,
      isSelected,
      setCount,
      onClick,
      onDelete,
      onLongPress,
      isDragging = false,
      isOverlay = false,
      dragHandleAttributes,
      dragHandleListeners,
      compact = false,
      className = '',
    },
    ref
  ) {
    const isGhost = isDragging && !isOverlay;
    const isActivelyDragged = isDragging && isOverlay;
    const longPressHandlers = useLongPress({
      threshold: 500,
      onLongPress: () => onLongPress?.(),
      onClick,
      disabled: !onLongPress,
    });

    // Presses that start on a nested button (drag handle, delete) belong to
    // that button: the handle's keys feed dnd-kit's keyboard sensor, and the
    // delete button activates on Space/Enter. The long-press handlers fire on
    // pointerup, so the buttons' click stopPropagation can't stop them.
    const pointerHandlers: React.HTMLAttributes<HTMLDivElement> = onLongPress
      ? {
          ...longPressHandlers,
          onPointerDown: (e) => {
            if (!isFromNestedButton(e)) longPressHandlers.onPointerDown(e);
          },
          onPointerUp: (e) => {
            if (isFromNestedButton(e)) longPressHandlers.onPointerCancel(e);
            else longPressHandlers.onPointerUp(e);
          },
        }
      : { onClick };

    // Keyboard handler for selection mode (press 'g' to group)
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.target !== e.currentTarget) return;
        // 'g' or 'G' for group - triggers selection mode
        if ((e.key === 'g' || e.key === 'G') && onLongPress) {
          e.preventDefault();
          onLongPress();
        }
        // Enter or Space to click/select
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      },
      [onClick, onLongPress]
    );

    return (
      <div
        ref={ref}
        data-testid="exercise-row"
        data-exercise-uuid={exercise.uuid}
        data-exercise-name={exercise.name}
        role="button"
        tabIndex={0}
        aria-label={`${exercise.name}. ${setCount} set${setCount !== 1 ? 's' : ''}. Press G to start grouping.`}
        onKeyDown={handleKeyDown}
        {...pointerHandlers}
        className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all focus:outline-none focus-visible:ring-state-focus ${
          isSelected
            ? 'bg-sakura-wash border border-stroke-accent'
            : 'bg-contrast-700 border border-transparent hover:bg-contrast-600'
        } ${isGhost ? 'opacity-40 border-dashed drag-ghost-stripes' : ''} ${
          isActivelyDragged
            ? 'rotate-[-0.5deg] translate-y-[-2px] drag-overlay-glow border-primary-300 z-10'
            : ''
        } ${className}`}
      >
        {/* Drag handle - 44px touch target per PRD, keyboard accessible with Space/Enter */}
        <button
          type="button"
          data-testid="exercise-drag-handle"
          className="flex items-center justify-center w-11 h-11 -ml-2 text-neutral-600 hover:text-neutral-400 cursor-grab active:cursor-grabbing select-none touch-manipulation focus:outline-none focus-visible:ring-state-focus rounded"
          aria-label={`Drag to reorder ${exercise.name}. Press Space or Enter to start dragging, then use arrow keys to move.`}
          aria-roledescription="draggable"
          onClick={(e) => e.stopPropagation()}
          {...dragHandleAttributes}
          {...dragHandleListeners}
        >
          <span className="text-body-sm" aria-hidden="true">
            ⋮⋮
          </span>
        </button>

        {/* Content - min-w-[7rem] ensures ~14 chars visible before truncation */}
        <div className={`flex-1 min-w-0 ${compact ? 'min-w-[7rem]' : ''}`}>
          <div className="text-body-md font-medium text-neutral-50 truncate">
            {exercise.name}
          </div>
        </div>

        {/* Set count badge - hidden in compact mode (first to drop) */}
        {setCount > 0 && !compact && (
          <Badge variant="primary" appearance="soft" size="sm" caps>
            {setCount} SET{setCount !== 1 && 'S'}
          </Badge>
        )}

        {/* Delete button (always visible on mobile, hover on desktop) */}
        <IconButton
          variant="ghost"
          size="xs"
          icon={<TrashIcon />}
          aria-label="Delete exercise"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 !text-neutral-500 hover:!text-error-400 hover:!bg-error-500/10"
        />

        {/* Selection indicator - hidden in compact mode (second to drop after badge) */}
        {!compact && (
          <span
            className={`text-body-md ${isSelected ? 'text-primary-300' : 'text-neutral-600'}`}
          >
            {isSelected ? '●' : '›'}
          </span>
        )}
      </div>
    );
  })
);

export default ExerciseRow;
